import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowUpRight, Users, Target, Lightbulb, Cpu, Award, Globe } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    document.title = 'About Us | Squarex Studios';

    const ctx = gsap.context(() => {
      // Hero animations
      gsap.fromTo(
        '.about-hero-title',
        { opacity: 0, y: 80 },
        { opacity: 1, y: 0, duration: 1, ease: 'power4.out' }
      );
      gsap.fromTo(
        '.about-hero-subtitle',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, delay: 0.2, ease: 'power3.out' }
      );

      // Content sections animation
      gsap.utils.toArray('.about-section').forEach((section) => {
        gsap.fromTo(
          section,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 85%',
            },
          }
        );
      });


    });

    return () => ctx.revert();
  }, []);



  const values = [
    {
      icon: Lightbulb,
      title: 'Innovation First',
      description: 'We embrace emerging technologies and creative approaches to solve complex problems.',
    },
    {
      icon: Target,
      title: 'Results Driven',
      description: 'Every decision we make is focused on delivering measurable outcomes for our clients.',
    },
    {
      icon: Users,
      title: 'Collaborative Spirit',
      description: 'We work as an extension of your team, not just an external vendor.',
    },
    {
      icon: Award,
      title: 'Excellence Standard',
      description: 'We hold ourselves to the highest standards of quality in everything we create.',
    },
  ];

  const capabilities = [
    { name: 'Shopify Plus Partners', description: 'Certified experts in enterprise e-commerce' },
    { name: 'AI Visual Production', description: 'Proprietary SQRX ShotLab technology' },
    { name: 'Design Systems', description: 'Scalable, component-based design approaches' },
    { name: 'Video Production', description: 'In-house editing and motion graphics' },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-[60vh] flex items-center pt-32 pb-20 hero-gradient"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="about-hero-subtitle text-[#af0000] font-mono text-sm uppercase tracking-widest mb-6">
            About Us
          </p>
          <h1 className="about-hero-title text-5xl md:text-6xl lg:text-7xl font-bold font-['Syne'] tracking-tighter leading-[0.95] max-w-4xl">
            A creative studio for the{' '}
            <span className="text-[#af0000]">digital age</span>
          </h1>
          <p className="about-hero-subtitle text-xl text-[#94a3b8] mt-8 max-w-2xl">
            We're designers, developers, and creative technologists united by a passion for building exceptional digital experiences.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center about-section">
            <div>
              <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">
                Our Story
              </p>
              <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight mb-6">
                Born from a vision to do things differently
              </h2>
              <div className="space-y-6 text-[#94a3b8] text-lg leading-relaxed">
                <p>
                  Squarex Studios was founded in 2019 with a simple belief: that great design and cutting-edge technology should work together seamlessly to create experiences that truly move people.
                </p>
                <p>
                  What started as a small team of three has grown into a full-service creative studio, but our core philosophy remains unchanged. We believe in the power of design to transform businesses, and we're committed to pushing the boundaries of what's possible.
                </p>
                <p>
                  Today, we're proud to work with brands across industries, from ambitious startups to established enterprises, helping them build memorable digital presences that drive real results.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] bg-[#1a1a1a] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=1000&fit=crop"
                  alt="Squarex Studios Team"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 border-2 border-[#af0000] -z-10" />
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#af0000]/20 -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16 about-section">
            <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">
              Our Philosophy
            </p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight">
              What we believe in
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 about-section">
            {values.map((value, index) => (
              <div
                key={index}
                className="p-8 bg-white/5 border border-white/10 hover:border-[#af0000]/30 transition-all"
              >
                <div className="w-12 h-12 bg-[#af0000]/20 flex items-center justify-center mb-6">
                  <value.icon size={24} className="text-[#af0000]" />
                </div>
                <h3 className="text-xl font-bold font-['Syne'] mb-3">{value.title}</h3>
                <p className="text-[#94a3b8] text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Capabilities Section */}
      <section className="py-24 md:py-32 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="about-section">
              <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">
                Studio Capabilities
              </p>
              <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight mb-6">
                What makes us different
              </h2>
              <p className="text-[#94a3b8] text-lg leading-relaxed mb-8">
                We combine creative excellence with technical expertise and cutting-edge AI technology to deliver solutions that other agencies simply can't match.
              </p>
              <div className="space-y-6">
                {capabilities.map((cap, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#af0000] flex items-center justify-center flex-shrink-0 mt-1">
                      <Cpu size={16} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold font-['Syne'] mb-1">{cap.name}</h4>
                      <p className="text-[#94a3b8] text-sm">{cap.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="about-section grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-square bg-[#1a1a1a] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=400&fit=crop"
                    alt="Our Work"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-[3/4] bg-[#1a1a1a] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=533&fit=crop"
                    alt="Our Work"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-[3/4] bg-[#1a1a1a] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1559028012-481c04fa702d?w=400&h=533&fit=crop"
                    alt="Our Work"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square bg-[#1a1a1a] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=400&h=400&fit=crop"
                    alt="Our Work"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center about-section">
          <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight mb-6">
            Ready to work with us?
          </h2>
          <p className="text-xl text-[#94a3b8] mb-10">
            Let's create something extraordinary together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="about-cta-btn"
              className="btn-primary inline-flex items-center justify-center gap-2"
            >
              Start a Project
              <ArrowUpRight size={18} />
            </Link>
            <Link
              to="/clients"
              className="btn-secondary inline-flex items-center justify-center gap-2"
            >
              View Our Work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
